import * as t from 'modules/dashboard/utils/dataTypes';
import * as widgetTypes from 'modules/dashboard/utils/dashboardWidgetTypes';
import { getNewDate, modifyDateUnit } from 'utils/date';
import { isMetricPercent } from 'utils/metrics';
import { generateRandomInteger } from 'utils/helpers';
import { MOCK_METRICS, MOCK_SETTINGS } from './DashboardMockConstants';
import { getGenerativeSettings, getMockSeriesSegmentCount } from './helpers';

/* Test Widgets */
const createWidget = (w, h, x, y, settings, type = widgetTypes.METRIC) => ({
  w,
  h,
  x,
  y,
  type,
  settings: { ...settings, yAxis: { title: '' } },
});

const widget1 = createWidget(3, 2, 0, 0, {
  metric: MOCK_METRICS.clickRate,
  visualization: 'number',
  invertColors: 0,
  showTrend: 1,
  trendDirection: 'up',
});
const widget2 = createWidget(3, 2, 0, 2, {
  metric: MOCK_METRICS.deliveryRate,
  visualization: 'number',
  invertColors: 0,
  showTrend: 1,
  trendDirection: 'up',
});
const widget3 = createWidget(3, 2, 0, 4, {
  metric: MOCK_METRICS.totalDelivered,
  visualization: 'number',
  invertColors: 0,
  showTrend: 1,
  showSparkline: true,
  trendDirection: 'up',
});
const widget4 = createWidget(6, 2, 0, 6, {
  metric: MOCK_METRICS.totalOpens,
  visualization: 'spline',
});
const widget5 = createWidget(3, 3, 3, 0, {
  metric: MOCK_METRICS.revenue,
  visualization: 'area',
});
const widget6 = createWidget(3, 3, 6, 0, {
  metric: MOCK_METRICS.totalPurchases,
  visualization: 'bar',
});
const widget7 = createWidget(3, 4, 9, 0, {
  metric: MOCK_METRICS.totalUnsubscribes,
  visualization: 'column',
});
const widget8 = createWidget(3, 2, 9, 4, {
  metric: MOCK_METRICS.totalBounces,
  visualization: 'line',
});
const widget9 = createWidget(6, 6, 6, 6, {
  metric: MOCK_METRICS.totalSent,
  visualization: 'area',
});
const widget10 = createWidget(
  6,
  3,
  3,
  3,
  {
    metric: MOCK_METRICS.null,
    visualization: 'campaignsTable',
    type: 'campaignsTable',
    columns: ['id', 'name', 'campaignState', 'messageMedium'],
    fetchParams: {
      page: 1,
      pageSize: 15,
      orderBy: 'id',
      sorting: 'desc',
    },
  },
  widgetTypes.CAMPAIGNS_TABLE,
);

/* Test Dashboard Data */
export const dashboardData = {
  settings: MOCK_SETTINGS,
  widgets: [
    widget1,
    widget2,
    widget3,
    widget4,
    widget5,
    widget6,
    widget7,
    widget8,
    widget9,
    widget10,
  ],
};

export const dashboardDataReset = {
  settings: MOCK_SETTINGS,
  widgets: [],
};

/**
 * generateSeriesData
 * Generates mockedSeries data using a provided config object (./DashboardMockConstants) and the props input.
 * @param { Object } props: the props object of the mocked widget,
 * @returns { Object } The generative mocked data for the series-style widget.
 */
export const generateSeriesData = (props = {}) => {
  const { start, increase, randomize, modifierFn } = getGenerativeSettings(props).series;
  const amount = getMockSeriesSegmentCount(props);
  let date = getNewDate({ date: props.startTime });
  let value = generateRandomInteger({ ...start });

  const buckets = [];
  Array.from({ length: amount }).forEach(() => {
    buckets.push({
      time: date.valueOf(),
      value: !modifierFn ? value : Math.floor(modifierFn({ value, date })),
    });

    value = randomize
      ? generateRandomInteger({ ...start })
      : generateRandomInteger({ max: value + generateRandomInteger({ ...increase }), min: value });

    date = modifyDateUnit(date);
  });

  return { histogram: { buckets } };
};

/**
 * generateComparisonData
 * Generates mockedSeries data using a provided config object (./DashboardMockConstants) and the props input.
 * @param { Object } props: the props object of the mocked widget,
 * @returns { Object } The generative mocked data for the comparison-style widget.
 */
export const generateComparisonData = (props = { metric: {} }) => {
  const { metric } = props;
  const isPercent = isMetricPercent(metric);
  const { max, min } = getGenerativeSettings(props).comparison;

  const data = Array.from({ length: generateRandomInteger({ max: 2, min: 1 }) }).map(() => {
    const lowNum = generateRandomInteger({ max, min });
    const largeNum = generateRandomInteger({ max, min: lowNum });
    const previousData = { value: isPercent ? lowNum / 100 : lowNum };
    const segmentData = {
      ...generateSeriesData({ dataType: 'series' }),
      value: isPercent ? largeNum / 100 : largeNum,
    };

    return { data: segmentData, previousData, metric };
  });
  return { metrics: data };
};

/**
 * getDashboardWidgetData
 * Generates mocked generative data for the requested widget.
 * @param { Object } props: the props object of the mocked widget,
 * @returns { Object } The generative mocked data for the widget.
 */
export const getDashboardWidgetData = (props = {}) => {
  const { dataType, metric } = props;
  const maxPercentageValue = generateRandomInteger({ max: 5000, min: 1 });
  switch (dataType) {
    case t.COMPARISON:
      return generateComparisonData(props);
    case t.PERCENTAGE:
      return {
        numerator: generateRandomInteger({ max: maxPercentageValue, min: 1 }),
        denominator: maxPercentageValue,
      };
    case t.SERIES:
    default:
      return {
        metrics: [
          {
            data: generateSeriesData(props),
            metric,
          },
        ],
      };
  }
};
